import { RadioClient } from './transport/radioClient';
import { LocationTracker } from './geo/locationTracker';
import { SessionManager } from './storage/sessionManager';
import { FusionEngine } from './storage/fusionEngine';
import { MapManager } from './map/mapManager';
import { FilterEngine } from './map/filterEngine';
import { TracerouteEngine } from './tx/tracerouteEngine';
import { Dashboard } from './ui/dashboard';
import { registerServiceWorker } from './pwa/registerSw';
import { requestPersistentStorage } from './storage/persistence';

/**
 * Bootstrap function: initializes the database, hardware abstraction layer,
 * geolocation tracking, Leaflet tactical map, and UI dashboard.
 */
async function bootstrap(): Promise<void> {
  console.info('[NodeRadar] Bootstrapping client-side engine...');

  // 1. Core Services
  const locationTracker = new LocationTracker();
  const sessionManager = new SessionManager();
  const radioClient = new RadioClient();

  // Initialize session and pre-existing state
  const activeSession = await sessionManager.init();
  console.info(`[NodeRadar] Active session loaded: ${activeSession.name} (${activeSession.id})`);

  // 2. Data Fusion and Map Engine
  const fusionEngine = new FusionEngine(sessionManager, locationTracker);
  const mapManager = new MapManager(fusionEngine);
  const filterEngine = new FilterEngine(mapManager);

  // 3. Auto Traceroute Engine
  const tracerouteEngine = new TracerouteEngine(radioClient, locationTracker, sessionManager);

  // 4. UI Dashboard Controller
  const dashboard = new Dashboard(
    radioClient,
    locationTracker,
    sessionManager,
    fusionEngine,
    mapManager,
    filterEngine,
    tracerouteEngine
  );

  // Wire incoming radio packets to data fusion engine
  radioClient.on('packet', (packet) => {
    fusionEngine.ingestPacket(packet).catch((err) => {
      console.error('[NodeRadar] Failed to ingest radio packet:', err);
    });
  });

  // Wire incoming NodeInfo discovery packets to NodeDB storage
  radioClient.on('nodeInfo', (nodeInfo) => {
    fusionEngine.ingestNodeInfo(nodeInfo).catch((err) => {
      console.error('[NodeRadar] Failed to ingest NodeInfo:', err);
    });
  });

  // Wire node position updates directly to map markers
  fusionEngine.onNodeUpdate((node) => {
    mapManager.updateNodeMarker(node);
  });

  // Initialize Leaflet map and UI
  mapManager.init('map-container');
  await dashboard.init();

  // Load existing known nodes from IndexedDB onto the map
  const existingNodes = fusionEngine.getAllKnownNodes();
  mapManager.renderAllKnownNodes(existingNodes);

  // Load existing session packets onto the map view
  await filterEngine.setSession(activeSession.id);

  // Start high-accuracy GPS tracking
  locationTracker.start();

  // Register ServiceWorker for offline standalone PWA capabilities in production
  registerServiceWorker('./sw.js').catch((err) => {
    console.warn('[NodeRadar] ServiceWorker registration notice:', err);
  });

  // Request persistent storage to safeguard telemetry from mobile browser eviction
  requestPersistentStorage().then((persisted) => {
    if (persisted) {
      console.info('[NodeRadar] IndexedDB storage persistence active.');
    }
  });

  console.info('[NodeRadar] Engine initialized and ready.');
}

window.addEventListener('DOMContentLoaded', () => {
  bootstrap().catch((err) => {
    console.error('[NodeRadar] Bootstrap initialization failed:', err);
  });
});
